'use client'

import { CraftAssetField } from 'tsconfig/craft-types'
import { useSiteInfo } from 'shared'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { MArticleLink } from '../molecules/m-article-link'
import { AButton } from '../atoms/a-button'
import './c-article-listing.css'

type Article = {
  id?: string
  title?: string
  typeHandle?: string
  uri?: string
  testimonialOccupation?: string
  image?: Array<CraftAssetField>
}

export type CArticleListingBlock = {
  id?: string
  typeHandle?: string
  idName?: string
  trendingArticles: Array<Article>
}

type Props = {
  field: Array<CArticleListingBlock>
  excludeId?: number
  /** First page of blog articles (excluding the trending ones), fetched by the server */
  articles?: Array<Article>
  /** Number of blog articles, to show "more articles" */
  articlesCount?: number
  /** Endpoint that returns more articles as { articles } */
  articlesUrl?: string
}

export const CArticleListing = ({ field, articles: initialArticles, articlesCount, articlesUrl }: Props) => {
  const { t } = useTranslation('common')
  const siteInfo = useSiteInfo()

  const content = useMemo(() => {
    return field && field.length ? field[0] : null
  }, [field])

  const excludeIds = useMemo(() => {
    return (
      content?.trendingArticles
        ?.filter((article) => {
          return (
            article.typeHandle === 'blog' || article.typeHandle === 'legacyBlog'
          )
        })
        .map((article) => {
          return article.id
        }) || []
    )
  }, [content])

  const getArticleTitle = (article: Article) => {
    if (article.typeHandle === 'testimonialPages') {
      const occupation = article.testimonialOccupation?.trim()
      return occupation && occupation !== article.title?.trim()
        ? `Meet ${occupation} ${article.title}`
        : `Meet ${article.title}`
    } else if (['blog', 'legacyBlog'].includes(article.typeHandle ?? '')) {
      return article.title
    }

    return ''
  }

  // The first page comes from the server (articles); more pages load from articlesUrl
  const [articles, setArticles] = useState<Array<Article>>(initialArticles ?? [])

  useEffect(() => {
    setArticles(initialArticles ?? [])
  }, [initialArticles])

  const fetchMoreArticles = async () => {
    const params = new URLSearchParams({
      kind: 'blog',
      site: siteInfo.handle,
      exclude: excludeIds.join(','),
      offset: String(articles.length),
      limit: '9',
    })
    const response = await fetch(`${articlesUrl ?? '/api/articles'}?${params}`)
    if (!response.ok) return
    const { articles: more } = (await response.json()) as { articles: Array<Article> }
    setArticles((previous) => [...previous, ...more])
  }

  return (
    <section className="c-article-listing mt-8 md:mt-20 mb-14 md:mb-27">
      <div className="o-container o-container--xl">
        <h2 className="text-h6 md:text-h4 text-neutral-dark-gray">
          {t('trending-news')}
        </h2>
        <div className="c-article-listing__trending mt-10 md:grid md:grid-cols-2 md:gap-4">
          {content?.trendingArticles.map((article: Article) => {
            return (
              <MArticleLink
                key={article.id}
                className="c-article-listing__trending-item"
                url={`/${article.uri}`}
                heading={getArticleTitle(article) ?? ''}
                image={article.image?.length ? article.image[0].url : null}
                imageAlt={article.image?.length ? article.image[0].title : null}
                linkText={t('read-article') ?? 'Read Story'}
              />
            )
          })}
        </div>
        <div className="c-article-listing__articles flex flex-wrap">
          {articles.map((article: Article) => {
            return (
              <MArticleLink
                key={article.id}
                className="c-article-listing__articles-item"
                url={`/${article.uri}`}
                heading={getArticleTitle(article) ?? ''}
                image={article.image?.length ? article.image[0].url : null}
                imageAlt={article.image?.length ? article.image[0].title : null}
                linkText={t('read-article') ?? 'Read Story'}
              />
            )
          })}
        </div>
        {(articles.length ?? 0) < (articlesCount ?? 0) && (
          <div className="flex justify-center mt-14">
            <AButton
              type="button"
              style="solid"
              color="black"
              size="lg"
              onClick={fetchMoreArticles}
            >
              <span>{t('more-articles')}</span>
            </AButton>
          </div>
        )}
      </div>
    </section>
  )
}

export { CArticleListingFragments } from '../../fragments/c-article-listing'
